import React, { useState, useEffect } from "react";
import {
  PiggyBank,
  TrendingUp,
  DollarSign,
  Target,
  Info,
  Calendar,
  Calculator,
  Check,
  Shield,
  Scale,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";

import Card from "./common/Card";
import GlowArea from "./charts/GlowArea";
import useChartTheme from "./charts/useChartTheme";

/* v4 section-label recipe (spec §4 "Section label"). */
const LABEL = "text-label uppercase text-arc-ink-4";

/* Amber pill used for the hypothetical-projection markers. */
const ESTIMATE_BADGE =
  "inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold text-arc-warn px-2 py-1 rounded-arc-sm bg-arc-warn-soft ring-1 ring-arc-warn/40";

/* Upper bound on a typed hypothetical starting balance. */
const MAX_ESTIMATED_PORTFOLIO = 100_000_000;

/* A saved estimate coming back from the member's settings, normalised to a
   number the field and the projection can trust — or null when it was never
   set, or came back malformed. Keeps a bad stored value from turning the
   input into NaN on restore (see ARC-23). */
const readSavedEstimate = (raw) => {
  if (raw == null || raw === "") return null;
  const n = typeof raw === "number" ? raw : parseFloat(raw);
  if (!isFinite(n) || n < 0) return null;
  return Math.min(n, MAX_ESTIMATED_PORTFOLIO);
};

/* Retirement-age bounds — unchanged from the input's old min/max, but now
   applied on blur instead of per keystroke (see ARC-25). */
const RETIRE_AGE_MIN = 0;
const RETIRE_AGE_MAX = 120;

/* ─── DobPicker — 3 independent dropdowns, never auto-advances ────────── */
const MONTHS_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 121 }, (_, i) => CURRENT_YEAR - i);

const daysInMonth = (year, monthIndex) => {
  if (monthIndex == null || year == null) return 31;
  return new Date(year, monthIndex + 1, 0).getDate();
};

function DobPicker({ onSave }) {
  const [month, setMonth] = React.useState("");
  const [day, setDay] = React.useState("");
  const [year, setYear] = React.useState("");

  const ready = month !== "" && day !== "" && year !== "";
  const dayMax = daysInMonth(year ? parseInt(year) : null, month !== "" ? parseInt(month) : null);
  const dayOptions = Array.from({ length: dayMax }, (_, i) => i + 1);

  const handleSave = () => {
    if (!ready) return;
    // Build ISO date string YYYY-MM-DD
    const m = String(parseInt(month) + 1).padStart(2, "0");
    const d = String(parseInt(day)).padStart(2, "0");
    const iso = `${year}-${m}-${d}`;
    onSave(iso);
  };

  return (
    <Card hover={false}>
      <div className="flex items-start gap-3 mb-4">
        <Calendar size={18} className="text-arc-warn flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-arc-ink font-medium text-sm mb-1">
            Set your date of birth once
          </p>
          <p className="text-arc-ink-3 text-xs">
            We'll auto-calculate your current age. You won't need to re-enter it.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col">
          <span className={`${LABEL} mb-2`}>Month</span>
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="px-3 py-2 rounded-arc-ctl text-sm min-w-[140px]"
          >
            <option value="">Select month</option>
            {MONTHS_FULL.map((m, i) => (
              <option key={i} value={i}>{m}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col">
          <span className={`${LABEL} mb-2`}>Day</span>
          <select
            value={day}
            onChange={(e) => setDay(e.target.value)}
            className="px-3 py-2 rounded-arc-ctl text-sm min-w-[80px] tnum"
          >
            <option value="">Day</option>
            {dayOptions.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </label>

        <label className="flex flex-col">
          <span className={`${LABEL} mb-2`}>Year</span>
          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="px-3 py-2 rounded-arc-ctl text-sm min-w-[100px] tnum"
          >
            <option value="">Year</option>
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>

        <button
          onClick={handleSave}
          disabled={!ready}
          className="bg-arc-accent text-white font-semibold rounded-arc-ctl px-4 py-2.5 shadow-arc-sm transition hover:brightness-105 active:brightness-95 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Check size={14} strokeWidth={2.5} />
          Save
        </button>
      </div>
    </Card>
  );
}

/**
 * Retirement Projection
 *
 * Accumulation phase + drawdown phase, with:
 *   - DOB-derived current age (saved once, no re-entry)
 *   - Growth rate input that survives backspace (independent string state)
 *   - Use-actual-DCA toggle (pulls from DCA tab)
 *   - 4% / 5% / fixed-years drawdown calculator
 *   - Current portfolio value = manual investments + on-chain wallets
 */
const RetirementTab = ({
  userAge,
  setUserAge,
  dob,
  setDob,
  netWorth,
  investments,
  wallets = [],
  netIncomeData,
  dcaData,
  investmentHistory,
  retirementSettings,
  setRetirementSettings,
}) => {
  /* ─── Parse a YYYY-MM-DD string as a LOCAL date (no timezone shift) ─ */
  const parseLocalDate = (iso) => {
    if (!iso) return null;
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const [, y, mo, d] = m.map(Number);
    return new Date(y, mo - 1, d);
  };

  /* ─── Derive age from DOB if available ────────────────────────────── */
  const ageFromDob = (dobStr) => {
    const d = parseLocalDate(dobStr);
    if (!d || isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age;
  };

  /* ─── Format DOB for display (avoiding the same timezone shift) ───── */
  const formatDob = (dobStr) => {
    const d = parseLocalDate(dobStr);
    if (!d || isNaN(d.getTime())) return dobStr || "";
    return d.toLocaleDateString();
  };

  // If DOB is set, age is derived. Otherwise we use the userAge state.
  const derivedAge = ageFromDob(dob);
  const effectiveAge = derivedAge != null ? derivedAge : userAge;

  /* ─── Local state — initialized from saved retirementSettings ─────── */
  const init = retirementSettings || {};
  const [retireAge, setRetireAge] = useState(init.retireAge ?? 65);
  // Text mirror of retireAge, so the field can sit empty or partial mid-edit
  // while the projection keeps reading the last valid number.
  const [retireAgeText, setRetireAgeText] = useState(
    String(init.retireAge ?? 65)
  );
  const [growthRate, setGrowthRate] = useState(init.growthRate ?? 0.07);
  const [growthRateText, setGrowthRateText] = useState(
    ((init.growthRate ?? 0.07) * 100).toFixed(2)
  );
  const [projection, setProjection] = useState([]);
  const [autoEstimate, setAutoEstimate] = useState(init.autoEstimate ?? false);
  const [timeRange, setTimeRange] = useState(init.timeRange ?? "all");
  const [contributionMode, setContributionMode] = useState(
    init.contributionMode ?? "simulated"
  );
  const [simulatedMonthly, setSimulatedMonthly] = useState(
    init.simulatedMonthly ?? ""
  );
  const [drawdownYears, setDrawdownYears] = useState(init.drawdownYears ?? 30);
  const [drawdownYearsText, setDrawdownYearsText] = useState(
    String(init.drawdownYears ?? 30)
  );
  const [drawdownReturn, setDrawdownReturn] = useState(init.drawdownReturn ?? 0.06);
  const [drawdownReturnText, setDrawdownReturnText] = useState(
    ((init.drawdownReturn ?? 0.06) * 100).toFixed(1)
  );

  /* ─── Portfolio-value mode — actual tracked balance vs. a hypothetical
        starting value the member types in. Both halves persist through
        retirementSettings, the same path every other input on this tab uses,
        so the choice and the typed figure come back on the member's next
        visit (ARC-23). Anything missing or unreadable falls back to the
        ARC-22 defaults: actual mode, blank field. ───────────────────────── */
  const savedEstimate = readSavedEstimate(init.estimatedPortfolioValue);
  const [portfolioMode, setPortfolioMode] = useState(
    init.portfolioValueMode === "estimated" ? "estimated" : "actual"
  );
  const [estimatedValueText, setEstimatedValueText] = useState(
    savedEstimate != null ? String(savedEstimate) : ""
  );
  // Last in-range value entered — the projection falls back to it while the
  // field is blank or mid-edit, so the chart never blanks out.
  const [lastValidEstimate, setLastValidEstimate] = useState(savedEstimate);
  // The typed figure as it goes back out to be saved. Trails the text field by
  // a debounce so a burst of keystrokes is one write, not one per character.
  const [persistedEstimate, setPersistedEstimate] = useState(savedEstimate);
  const isEstimateMode = portfolioMode === "estimated";

  /* ─── Chart glow accent — resolved amber (this tab's signature tone), used for
        the soft box-shadow bloom on stat badges (same DOM-glow idiom as the
        chart legend dots). Re-resolves when the brand theme flips. ─────────── */
  const chartTheme = useChartTheme();
  const amber = chartTheme.tone?.amber || "#E0A94A";

  /* ─── Hydrate when retirementSettings loads from Firestore ────────── */
  // If settings come in async (after mount), sync local state once.
  const hydratedRef = React.useRef(false);
  useEffect(() => {
    if (!retirementSettings || hydratedRef.current) return;
    if (Object.keys(retirementSettings).length === 0) return;
    hydratedRef.current = true;
    if (retirementSettings.retireAge != null) {
      setRetireAge(retirementSettings.retireAge);
      setRetireAgeText(String(retirementSettings.retireAge));
      restoreAgeRef.current = retirementSettings.retireAge;
      prevAgeTextRef.current = String(retirementSettings.retireAge);
    }
    if (retirementSettings.growthRate != null) {
      setGrowthRate(retirementSettings.growthRate);
      setGrowthRateText((retirementSettings.growthRate * 100).toFixed(2));
    }
    if (retirementSettings.autoEstimate != null) setAutoEstimate(retirementSettings.autoEstimate);
    if (retirementSettings.timeRange != null) setTimeRange(retirementSettings.timeRange);
    if (retirementSettings.contributionMode != null) setContributionMode(retirementSettings.contributionMode);
    if (retirementSettings.simulatedMonthly != null) setSimulatedMonthly(retirementSettings.simulatedMonthly);
    if (retirementSettings.drawdownYears != null) {
      setDrawdownYears(retirementSettings.drawdownYears);
      setDrawdownYearsText(String(retirementSettings.drawdownYears));
    }
    if (retirementSettings.drawdownReturn != null) {
      setDrawdownReturn(retirementSettings.drawdownReturn);
      setDrawdownReturnText((retirementSettings.drawdownReturn * 100).toFixed(1));
    }
    if (retirementSettings.portfolioValueMode != null) {
      setPortfolioMode(
        retirementSettings.portfolioValueMode === "estimated" ? "estimated" : "actual"
      );
    }
    const hydratedEstimate = readSavedEstimate(retirementSettings.estimatedPortfolioValue);
    if (hydratedEstimate != null) {
      setEstimatedValueText(String(hydratedEstimate));
      setLastValidEstimate(hydratedEstimate);
      setPersistedEstimate(hydratedEstimate);
    }
  }, [retirementSettings]);

  /* ─── Persist any change to parent → autosaves to Firestore ───────── */
  useEffect(() => {
    if (!setRetirementSettings) return;
    setRetirementSettings({
      retireAge,
      growthRate,
      autoEstimate,
      timeRange,
      contributionMode,
      simulatedMonthly,
      drawdownYears,
      drawdownReturn,
      portfolioValueMode: portfolioMode,
      estimatedPortfolioValue: persistedEstimate,
    });
  }, [
    retireAge,
    growthRate,
    autoEstimate,
    timeRange,
    contributionMode,
    simulatedMonthly,
    drawdownYears,
    drawdownReturn,
    portfolioMode,
    persistedEstimate,
    setRetirementSettings,
  ]);

  /* ─── Flatten investment history for realized growth calc ─────────── */
  const flattenInvestmentHistory = (history) => {
    if (!history || !Object.keys(history).length) return [];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const data = [];
    Object.keys(history)
      .sort((a, b) => Number(a) - Number(b))
      .forEach((year) => {
        months.forEach((m) => {
          if (history[year][m] !== undefined) {
            data.push({ yearMonth: `${m} ${year}`, value: history[year][m] });
          }
        });
      });
    const cleaned = data.filter((d) => typeof d.value === "number" && d.value > 0);
    if (timeRange !== "all") {
      const monthsToKeep = parseInt(timeRange);
      return cleaned.slice(-monthsToKeep);
    }
    return cleaned;
  };
  const historicalData = flattenInvestmentHistory(investmentHistory);

  /* ─── Realized growth rate (clamped) ──────────────────────────────── */
  const calculateRealizedGrowthRate = (data) => {
    if (data.length < 2) return 0.07;
    const startValue = data[0].value;
    const endValue = data[data.length - 1].value;
    const months = data.length - 1 || 1;
    const cagr = Math.pow(endValue / startValue, 12 / months) - 1;
    if (!isFinite(cagr)) return 0.07;
    return Math.max(-0.2, Math.min(0.15, cagr));
  };
  const realizedGrowthRate = calculateRealizedGrowthRate(historicalData);

  /* ─── Current Portfolio Value = manual investments + on-chain wallets ── */
  const totalInvestments = (investments || []).reduce(
    (s, i) => s + (i.amount || 0),
    0
  );
  const totalCrypto = (wallets || []).reduce(
    (s, w) =>
      s +
      (typeof w.manualValue === "number" && !isNaN(w.manualValue)
        ? w.manualValue
        : w.lastValue || 0),
    0
  );
  const currentPortfolioValue = totalInvestments + totalCrypto;

  /* ─── Starting balance the projection runs from ───────────────────────
        In "actual" mode this is the tracked portfolio, exactly as before.
        In "estimated" mode it's the member's typed figure — clamped for the
        math only; the text they're typing is left alone until blur. */
  const parseEstimate = (text) => {
    if (text === "" || text === ".") return null;
    const n = parseFloat(text);
    if (isNaN(n) || n < 0) return null;
    return Math.min(n, MAX_ESTIMATED_PORTFOLIO);
  };
  const typedEstimate = parseEstimate(estimatedValueText);
  const estimatedPortfolioValue = typedEstimate ?? lastValidEstimate ?? 0;
  const startingPortfolioValue = isEstimateMode
    ? estimatedPortfolioValue
    : currentPortfolioValue;

  /* Hand the typed figure to the saved settings only once typing pauses, so a
     burst of keystrokes settles into a single write. Depends on the parsed
     number rather than the raw text: mid-edit states that don't parse (""
     or ".") save as null, which is how a cleared field comes back blank. */
  useEffect(() => {
    const t = setTimeout(() => setPersistedEstimate(typedEstimate), 500);
    return () => clearTimeout(t);
  }, [typedEstimate]);

  /* ─── Average DCA from DCA tab ────────────────────────────────────── */
  const actualDcaMonthly =
    dcaData?.monthlyAmount > 0
      ? dcaData.monthlyAmount
      : dcaData?.history?.length
      ? dcaData.history.reduce((s, r) => s + (r.dcaContribution || 0), 0) /
        dcaData.history.length
      : 0;

  const monthlyContribution =
    contributionMode === "simulated" && simulatedMonthly && parseFloat(simulatedMonthly) > 0
      ? parseFloat(simulatedMonthly)
      : actualDcaMonthly;

  const yearsToRetire = Math.max(0, retireAge - effectiveAge);

  /* ─── Auto-estimate sync ──────────────────────────────────────────── */
  useEffect(() => {
    if (autoEstimate) {
      setGrowthRate(realizedGrowthRate);
      setGrowthRateText((realizedGrowthRate * 100).toFixed(2));
    }
  }, [autoEstimate, realizedGrowthRate]);

  /* ─── Accumulation projection ─────────────────────────────────────── */
  useEffect(() => {
    let future = startingPortfolioValue;
    const data = [];
    for (let y = 1; y <= yearsToRetire; y++) {
      future = (future + monthlyContribution * 12) * (1 + growthRate);
      data.push({
        yearMonth: `${new Date().getFullYear() + y}`,
        value: Math.round(future),
      });
    }
    setProjection(data);
  }, [retireAge, effectiveAge, growthRate, startingPortfolioValue, monthlyContribution, yearsToRetire]);

  const projectedValue =
    projection.length > 0 ? projection[projection.length - 1].value : startingPortfolioValue;

  /* ─── Drawdown phase math ─────────────────────────────────────────── */
  // 4% Rule: ~perpetual safe annual withdrawal
  const safeAnnual4 = projectedValue * 0.04;
  const safeAnnual5 = projectedValue * 0.05;

  // Fixed-period amortization: PMT = P * r / (1 - (1+r)^-n)
  // Where P=principal, r=monthly return rate, n=months
  const monthlyDrawdownReturn = drawdownReturn / 12;
  const drawdownMonths = drawdownYears * 12;
  const fixedMonthlyDraw =
    drawdownMonths > 0 && monthlyDrawdownReturn > 0
      ? (projectedValue * monthlyDrawdownReturn) /
        (1 - Math.pow(1 + monthlyDrawdownReturn, -drawdownMonths))
      : drawdownMonths > 0
      ? projectedValue / drawdownMonths
      : 0;
  const fixedAnnualDraw = fixedMonthlyDraw * 12;

  /* ─── Number formatting ───────────────────────────────────────────── */
  const fmtCurrency = (n) =>
    `$${(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  const fmtAxis = (v) => {
    const a = Math.abs(v);
    if (a >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (a >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
    return `$${v}`;
  };

  /* ─── Handlers ────────────────────────────────────────────────────── */
  /* Retirement age. Same text-mirror pattern as Drawdown Period below:
     keystrokes only move the text, so the field can be emptied and retyped;
     retireAge — what the projection reads — advances only when the text
     parses in range, and the bounds are enforced on blur (see ARC-25). */
  // What an emptied field falls back to on blur. It advances as the member
  // types digits but NOT as they delete them: backspacing "60" passes through
  // "6", and 6 must not become the restore point.
  const restoreAgeRef = React.useRef(retireAge);
  const prevAgeTextRef = React.useRef(String(retireAge));

  const handleRetireAgeText = (e) => {
    // Dropping everything but digits is what rejects "-" and "e": the old
    // type="number" input handed those over as an empty value.
    const txt = e.target.value.replace(/[^0-9]/g, "");
    const grew = txt.length > prevAgeTextRef.current.length;
    prevAgeTextRef.current = txt;
    setRetireAgeText(txt);
    if (txt === "") return;
    const n = parseInt(txt, 10);
    if (isNaN(n) || n < RETIRE_AGE_MIN || n > RETIRE_AGE_MAX) return;
    setRetireAge(n);
    if (grew) restoreAgeRef.current = n;
  };
  const handleRetireAgeBlur = () => {
    const n = parseInt(retireAgeText, 10);
    if (isNaN(n)) {
      // Empty or unparseable — restore the last valid value, not a default.
      const restored = restoreAgeRef.current;
      setRetireAge(restored);
      setRetireAgeText(String(restored));
      prevAgeTextRef.current = String(restored);
      return;
    }
    const clamped = Math.min(RETIRE_AGE_MAX, Math.max(RETIRE_AGE_MIN, n));
    setRetireAge(clamped);
    setRetireAgeText(String(clamped));
    restoreAgeRef.current = clamped;
    prevAgeTextRef.current = String(clamped);
  };

  const handleGrowthRateText = (e) => {
    const text = e.target.value;
    setGrowthRateText(text);
    // Allow empty / partial typing without forcing the value
    if (text === "" || text === "-" || text === ".") return;
    const parsed = parseFloat(text);
    if (!isNaN(parsed)) setGrowthRate(parsed / 100);
  };
  const handleGrowthRateBlur = () => {
    // Format to 2 decimals on blur if it's a number
    const parsed = parseFloat(growthRateText);
    if (!isNaN(parsed)) setGrowthRateText(parsed.toFixed(2));
  };

  /* Estimated-value input. Keystrokes only strip characters that can't belong
     to a currency amount — no clamping, no coercion — so the field stays
     freely clearable and retypeable (see ARC-25). Range is applied on blur. */
  const handleEstimatedValueChange = (e) => {
    const cleaned = e.target.value
      // Dropping "-" here is what rejects a negative: it can never be typed.
      .replace(/[^0-9.]/g, "")
      // Keep only the first decimal point.
      .replace(/^(\d*\.?\d*).*$/, "$1");
    setEstimatedValueText(cleaned);
    const parsed = parseEstimate(cleaned);
    if (parsed != null) setLastValidEstimate(parsed);
  };
  const handleEstimatedValueBlur = () => {
    const parsed = parseEstimate(estimatedValueText);
    if (parsed == null) {
      // Blank or unparseable — leave it empty; the projection falls back to
      // the last valid value entered this session, or 0 if there isn't one.
      setEstimatedValueText("");
      return;
    }
    setLastValidEstimate(parsed);
    setEstimatedValueText(String(parsed));
  };

  const handleDob = (e) => setDob && setDob(e.target.value);

  /* ─── Render ──────────────────────────────────────────────────────── */
  /* `text-left` on the root resets the app shell's inherited
     `text-align: center`, so structured blocks (stat tiles, icon-led cards)
     keep a shared left edge; the section headings and the free-standing body
     paragraphs opt back into `text-center` individually. Those paragraphs
     keep the `max-w-prose` cap from ARC-21 and add `mx-auto` so the capped
     block itself sits centered rather than hugging the left edge. */
  return (
    <div className="space-y-6 text-left">
      {/* Header */}
      <div>
        <p className={`${LABEL} mb-2`}>Projection</p>
        <h2 className="text-display text-arc-ink mb-2 text-center">Retirement</h2>
        <p className="text-arc-ink-2 text-sm leading-relaxed max-w-prose mx-auto text-center">
          Long-term projections based on your portfolio + DCA plan, plus a drawdown
          calculator showing what you can sustainably withdraw once you hit your number.
        </p>
      </div>

      {/* Date of Birth (saves once) */}
      {!dob && <DobPicker onSave={(iso) => handleDob({ target: { value: iso } })} />}
      {dob && (
        <div className="rounded-arc-ctl ring-1 ring-arc-line bg-arc-paper-2 px-4 py-2.5 flex items-center gap-3 text-xs">
          <Calendar size={14} className="text-arc-warn" />
          <span className="text-arc-ink-3">Date of birth on file:</span>
          <span className="tnum text-arc-ink">{formatDob(dob)}</span>
          <span className="text-arc-ink-4">·</span>
          <span className="tnum text-arc-ink-2">{effectiveAge} years old</span>
          <button
            onClick={() => setDob && setDob("")}
            className="ml-auto text-arc-ink-4 hover:text-arc-ink text-[10px] uppercase tracking-[0.14em] font-semibold transition-colors"
          >
            Edit
          </button>
        </div>
      )}

      {/* How-to box */}
      <Card hover={false} className="flex gap-3">
        <Info size={18} className="text-arc-warn flex-shrink-0 mt-0.5" />
        <div className="text-sm text-arc-ink-2 leading-relaxed">
          <p className="text-arc-ink font-medium mb-1.5">How to use this tool</p>
          <ul className="space-y-1">
            <li>
              <span className="text-arc-ink">Growth Period</span> — sample your
              actual portfolio history (3 / 6 months / all-time) for the realized growth rate.
            </li>
            <li>
              <span className="text-arc-ink">Auto-estimate</span> — fills growth rate from real data, clamped to 0–15%. Always overridable.
            </li>
            <li>
              <span className="text-arc-ink">Contribution mode</span> — use your actual DCA from the DCA tab, or simulate "what-if" amounts.
            </li>
            <li>
              <span className="text-arc-ink">Drawdown phase</span> — see what you can sustainably withdraw once you hit your number.
            </li>
          </ul>
        </div>
      </Card>

      {/* Assumptions — inputs + contribution mode */}
      <Card hover={false}>
        {/* Portfolio-value mode toggle — same control pattern as the
            contribution-mode toggle further down this card. */}
        <div className="mb-5 pb-5 border-b border-arc-line">
          <p className={`${LABEL} mb-3 text-center`}>Portfolio Value</p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setPortfolioMode("actual")}
              className={`flex items-center gap-2 px-4 py-2 rounded-arc-ctl text-sm font-medium ring-1 transition-all ${
                !isEstimateMode
                  ? "bg-arc-warn-soft text-arc-warn ring-arc-warn/50 shadow-arc-sm"
                  : "bg-arc-card text-arc-ink-3 ring-arc-line hover:text-arc-ink hover:bg-arc-paper-2"
              }`}
            >
              Use My Actual Portfolio
            </button>
            <button
              onClick={() => setPortfolioMode("estimated")}
              className={`flex items-center gap-2 px-4 py-2 rounded-arc-ctl text-sm font-medium ring-1 transition-all ${
                isEstimateMode
                  ? "bg-arc-warn-soft text-arc-warn ring-arc-warn/50 shadow-arc-sm"
                  : "bg-arc-card text-arc-ink-3 ring-arc-line hover:text-arc-ink hover:bg-arc-paper-2"
              }`}
            >
              Estimate a Value
            </button>
            <div className="flex-1 min-w-[200px]">
              {isEstimateMode ? (
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-arc-ink-4 text-sm">
                    $
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={estimatedValueText}
                    placeholder="0"
                    onChange={handleEstimatedValueChange}
                    onBlur={handleEstimatedValueBlur}
                    className="w-full pl-7 pr-3 py-2 rounded-arc-ctl text-sm tnum"
                  />
                </div>
              ) : (
                <div className="px-3 py-2 rounded-arc-ctl bg-arc-paper-2 ring-1 ring-arc-line text-sm">
                  <span className="text-arc-ink-3">Tracked portfolio: </span>
                  <span className="tnum text-arc-warn">
                    {fmtCurrency(currentPortfolioValue)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Current age */}
          <label className="flex flex-col">
            <span className={`${LABEL} mb-2 text-center`}>
              Current Age{derivedAge != null ? " (from DOB)" : ""}
            </span>
            <input
              type="number"
              min={0}
              max={120}
              value={effectiveAge}
              onChange={(e) =>
                !derivedAge && setUserAge(parseInt(e.target.value) || 0)
              }
              disabled={derivedAge != null}
              className="px-3 py-2 rounded-arc-ctl text-sm tnum"
            />
          </label>

          {/* Retirement age */}
          <label className="flex flex-col">
            <span className={`${LABEL} mb-2 text-center`}>Retirement Age</span>
            <input
              type="text"
              inputMode="numeric"
              value={retireAgeText}
              onChange={handleRetireAgeText}
              onBlur={handleRetireAgeBlur}
              className="px-3 py-2 rounded-arc-ctl text-sm tnum"
            />
          </label>

          {/* Annual growth rate (with backspace fix) */}
          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <span className={LABEL}>Annual Growth Rate</span>
              <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.04em] font-semibold text-arc-ink-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={autoEstimate}
                  onChange={() => setAutoEstimate(!autoEstimate)}
                  className="accent-arc-warn"
                />
                Auto
              </label>
            </div>
            <div className="relative">
              <input
                type="text"
                inputMode="decimal"
                value={growthRateText}
                onChange={handleGrowthRateText}
                onBlur={handleGrowthRateBlur}
                className="w-full px-3 py-2 pr-7 rounded-arc-ctl text-sm tnum"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-arc-ink-4 text-sm">
                %
              </span>
            </div>
          </div>

          {/* Growth period selector */}
          <label className="flex flex-col">
            <span className={`${LABEL} mb-2 text-center`}>Growth Period</span>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-2 rounded-arc-ctl text-sm"
            >
              <option value="all">All-time</option>
              <option value="3">Last 3 months</option>
              <option value="6">Last 6 months</option>
            </select>
          </label>
        </div>

        {/* Contribution mode toggle */}
        <div className="mt-5 pt-5 border-t border-arc-line">
          <p className={`${LABEL} mb-3 text-center`}>Monthly Contribution</p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setContributionMode("actual")}
              className={`flex items-center gap-2 px-4 py-2 rounded-arc-ctl text-sm font-medium ring-1 transition-all ${
                contributionMode === "actual"
                  ? "bg-arc-warn-soft text-arc-warn ring-arc-warn/50 shadow-arc-sm"
                  : "bg-arc-card text-arc-ink-3 ring-arc-line hover:text-arc-ink hover:bg-arc-paper-2"
              }`}
            >
              Use My Actual DCA
            </button>
            <button
              onClick={() => setContributionMode("simulated")}
              className={`flex items-center gap-2 px-4 py-2 rounded-arc-ctl text-sm font-medium ring-1 transition-all ${
                contributionMode === "simulated"
                  ? "bg-arc-warn-soft text-arc-warn ring-arc-warn/50 shadow-arc-sm"
                  : "bg-arc-card text-arc-ink-3 ring-arc-line hover:text-arc-ink hover:bg-arc-paper-2"
              }`}
            >
              Simulate "What If"
            </button>
            <div className="flex-1 min-w-[200px]">
              {contributionMode === "actual" ? (
                <div className="px-3 py-2 rounded-arc-ctl bg-arc-paper-2 ring-1 ring-arc-line text-sm">
                  <span className="text-arc-ink-3">Pulled from DCA tab: </span>
                  <span className="tnum text-arc-warn">{fmtCurrency(actualDcaMonthly)}</span>
                  <span className="text-arc-ink-3">/mo</span>
                </div>
              ) : (
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-arc-ink-4 text-sm">
                    $
                  </span>
                  <input
                    type="number"
                    min={0}
                    value={simulatedMonthly}
                    placeholder="Enter monthly amount"
                    onChange={(e) => setSimulatedMonthly(e.target.value)}
                    className="w-full pl-7 pr-3 py-2 rounded-arc-ctl text-sm tnum"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Chart — growth projection as a gains-grade GlowArea (amber tone) */}
      <Card hover={false}>
        <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
          <h3 className="text-h2 text-arc-ink">Growth Projection</h3>
          {isEstimateMode && <span className={ESTIMATE_BADGE}>Estimate</span>}
        </div>
        <GlowArea
          data={projection}
          xKey="yearMonth"
          height={350}
          series={[{ key: "value", name: "Projected", tone: "amber" }]}
          yTickFormatter={fmtAxis}
          valueFormatter={fmtCurrency}
        />
        {isEstimateMode && (
          <p className="mt-3 text-xs text-arc-ink-3 leading-relaxed max-w-prose mx-auto text-center">
            This projection uses an estimated starting portfolio value, not your
            tracked balance.
          </p>
        )}
      </Card>

      {/* Stats — accumulation phase */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card hover={false} className="flex items-center gap-3">
          <span
            className="inline-flex items-center justify-center w-10 h-10 rounded-arc-sm bg-arc-warn-soft flex-shrink-0"
            style={{ boxShadow: `0 0 12px ${amber}40` }}
          >
            <PiggyBank className="text-arc-warn" size={20} />
          </span>
          <div className="min-w-0">
            <p className={`${LABEL} mb-0.5`}>
              {isEstimateMode ? "Estimated Starting Value" : "Current Portfolio Value"}
            </p>
            <p className="text-stat tnum text-arc-ink truncate">{fmtCurrency(startingPortfolioValue)}</p>
            <p className="text-[10px] text-arc-ink-4 mt-0.5 tnum">
              {isEstimateMode
                ? `Tracked balance: ${fmtCurrency(currentPortfolioValue)}`
                : `${fmtCurrency(totalInvestments)} manual + ${fmtCurrency(totalCrypto)} on-chain`}
            </p>
          </div>
        </Card>

        <Card hover={false} className="flex items-center gap-3">
          <span
            className="inline-flex items-center justify-center w-10 h-10 rounded-arc-sm bg-arc-warn-soft flex-shrink-0"
            style={{ boxShadow: `0 0 12px ${amber}40` }}
          >
            <DollarSign className="text-arc-warn" size={20} />
          </span>
          <div className="min-w-0">
            <p className={`${LABEL} mb-0.5`}>
              {contributionMode === "actual" ? "Actual DCA / mo" : "Simulated DCA / mo"}
            </p>
            <p className="text-stat tnum text-arc-ink truncate">{fmtCurrency(monthlyContribution)}</p>
          </div>
        </Card>

        <Card hover={false} className="flex items-center gap-3">
          <span
            className="inline-flex items-center justify-center w-10 h-10 rounded-arc-sm bg-arc-warn-soft flex-shrink-0"
            style={{ boxShadow: `0 0 12px ${amber}40` }}
          >
            <TrendingUp className="text-arc-warn" size={20} />
          </span>
          <div className="min-w-0">
            <p className={`${LABEL} mb-0.5`}>{autoEstimate ? "Realized Growth" : "Manual Growth"}</p>
            <p className="text-stat tnum text-arc-ink truncate">{(growthRate * 100).toFixed(2)}%</p>
          </div>
        </Card>

        {/* Signature stat — projected number, amber hero with a stronger bloom */}
        <Card hover={false} className="flex items-center gap-3 ring-arc-warn/30 bg-arc-warn-soft">
          <span
            className="inline-flex items-center justify-center w-10 h-10 rounded-arc-sm bg-arc-warn/20 flex-shrink-0"
            style={{ boxShadow: `0 0 18px ${amber}66` }}
          >
            <Target className="text-arc-warn" size={20} />
          </span>
          <div className="min-w-0">
            <p className={`${LABEL} mb-0.5`}>Projected at {retireAge}</p>
            <p className="text-stat tnum text-arc-warn truncate">{fmtCurrency(projectedValue)}</p>
            <p className="text-[10px] text-arc-ink-4 mt-0.5">in {yearsToRetire} years</p>
          </div>
        </Card>
      </div>

      {/* DRAWDOWN PHASE — what you can sustainably withdraw */}
      <Card hover={false} className="sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <Calculator size={16} className="text-arc-warn" />
          <p className={LABEL}>Drawdown Phase</p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2 mb-2">
          <h3 className="text-h2 text-arc-ink">
            What you can withdraw once you hit your number
          </h3>
          {isEstimateMode && <span className={ESTIMATE_BADGE}>Estimate</span>}
        </div>
        <p className="text-arc-ink-2 text-sm leading-relaxed mb-5 max-w-prose mx-auto text-center">
          Based on your projected portfolio of{" "}
          <span className="tnum text-arc-warn">{fmtCurrency(projectedValue)}</span> at age{" "}
          <span className="tnum text-arc-ink">{retireAge}</span>. Pick a preset below or set
          your own assumptions in the inputs further down.
        </p>

        {/* Preset chips — one click sets drawdown years + return to a preset
            scenario. Active preset (matches current inputs) gets an amber ring.
            This replaces the old "three equal cards" UI which implied 4%/5%
            rules were competing options instead of historical benchmarks. */}
        {(() => {
          const presets = [
            {
              key: "conservative",
              label: "Conservative",
              Icon: Shield,
              years: 30,
              ret: 0.05,
              hint: "30 yrs · 5% return · low risk",
            },
            {
              key: "moderate",
              label: "Moderate",
              Icon: Scale,
              years: 25,
              ret: 0.07,
              hint: "25 yrs · 7% return · balanced",
            },
            {
              key: "aggressive",
              label: "Aggressive",
              Icon: TrendingUp,
              years: 20,
              ret: 0.10,
              hint: "20 yrs · 10% return · risk on",
            },
          ];
          const matchesPreset = (p) =>
            drawdownYears === p.years &&
            Math.abs(drawdownReturn - p.ret) < 0.0005;
          const applyPreset = (p) => {
            setDrawdownYears(p.years);
            setDrawdownYearsText(String(p.years));
            setDrawdownReturn(p.ret);
            setDrawdownReturnText((p.ret * 100).toFixed(1));
          };
          return (
            <div className="mb-5">
              <p className={`${LABEL} mb-2 text-center`}>Pick a scenario</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {presets.map((p) => {
                  const active = matchesPreset(p);
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => applyPreset(p)}
                      className={`text-center rounded-arc-ctl ring-1 p-3 transition-all ${
                        active
                          ? "ring-arc-warn bg-arc-warn-soft"
                          : "ring-arc-line bg-arc-paper-2 hover:ring-arc-warn/40 hover:bg-arc-warn-soft/60"
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <p.Icon
                          size={15}
                          className={active ? "text-arc-warn" : "text-arc-ink-3"}
                        />
                        <span
                          className={`font-semibold text-sm ${
                            active ? "text-arc-warn" : "text-arc-ink"
                          }`}
                        >
                          {p.label}
                        </span>
                        {active && (
                          <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-arc-warn">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-arc-ink-4">{p.hint}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Primary withdrawal card — the ONE answer based on current inputs.
            Adapts styling for high-return warning state. */}
        {(() => {
          const isHighReturn = drawdownReturn > 0.10;
          const annualInterest = projectedValue * drawdownReturn;
          const isPerpetuity =
            annualInterest > 0 &&
            Math.abs(fixedAnnualDraw - annualInterest) / annualInterest < 0.05;
          return (
            <div
              className={`rounded-arc-card ring-1 p-5 sm:p-6 mb-4 ${
                isHighReturn
                  ? "ring-arc-warn/50 bg-arc-warn-soft"
                  : "ring-arc-warn/30 bg-arc-warn-soft/60"
              }`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                <div>
                  <p className={`${LABEL} mb-1`}>Your annual withdrawal</p>
                  <p className="text-xs text-arc-ink-3">
                    {drawdownYears} years @ {(drawdownReturn * 100).toFixed(1)}% return
                  </p>
                </div>
                {isHighReturn && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold text-arc-warn px-2 py-1 rounded-arc-sm bg-arc-warn-soft ring-1 ring-arc-warn/40">
                    <AlertTriangle size={11} /> Aggressive assumption
                  </span>
                )}
              </div>
              <p className="tnum text-4xl sm:text-5xl text-arc-warn mb-2 leading-tight">
                {fmtCurrency(fixedAnnualDraw)}
              </p>
              <p className="text-sm text-arc-ink-3">
                per year · ≈{" "}
                <span className="tnum text-arc-ink">{fmtCurrency(fixedMonthlyDraw)}</span>{" "}
                per month
              </p>
              {!isHighReturn && isPerpetuity && (
                <p className="mt-3 text-[11px] uppercase tracking-[0.14em] font-semibold text-arc-warn">
                  ≈ pure interest at this rate · principal preserved
                </p>
              )}
            </div>
          );
        })()}

        {/* Benchmark strip — Bengen's 4% rule and 5% rule shown as historical
            references, NOT as competing options. Just sanity checks. */}
        <div className="mb-5 rounded-arc-ctl ring-1 ring-arc-line bg-arc-paper-2 p-3">
          <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-arc-ink-4 mb-2">
            For reference · historical benchmarks
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-arc-ink-3">
                <span className="font-semibold text-arc-ink">Bengen 4% rule</span> · 30-yr safe
              </span>
              <span className="tnum text-arc-warn whitespace-nowrap">
                {fmtCurrency(safeAnnual4)}/yr
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-arc-ink-3">
                <span className="font-semibold text-arc-ink">5% rule</span> · ~25-yr aggressive
              </span>
              <span className="tnum text-arc-warn whitespace-nowrap">
                {fmtCurrency(safeAnnual5)}/yr
              </span>
            </div>
          </div>
          <p className="text-[10px] text-arc-ink-4 mt-2 leading-relaxed">
            Bengen tested every 30-yr window in US market history. Withdrawing 4% of
            starting portfolio (inflation-adjusted) survived every scenario including
            the Great Depression. These are anchors — compare your number above against
            them as a reality check.
          </p>
        </div>

        {/* Perpetuity callout — only show when high-return input is causing
            the Fixed Period to be near pure interest. Helps the user see
            why their 40-yr number doesn't change much when they tweak years. */}
        {(() => {
          const annualInterest = projectedValue * drawdownReturn;
          const isPerpetuity =
            drawdownReturn > 0.10 &&
            annualInterest > 0 &&
            Math.abs(fixedAnnualDraw - annualInterest) / annualInterest < 0.05;
          if (!isPerpetuity) return null;
          return (
            <div className="mb-5 rounded-arc-ctl ring-1 ring-arc-warn/40 bg-arc-warn-soft p-3 flex items-start gap-2">
              <Info size={15} className="text-arc-warn flex-shrink-0 mt-0.5" />
              <p className="text-xs text-arc-ink-2 leading-relaxed">
                <span className="font-semibold text-arc-ink">Why doesn't the Fixed Period change much when you tweak years?</span>{" "}
                At {(drawdownReturn * 100).toFixed(0)}% return, your portfolio of{" "}
                <span className="tnum">{fmtCurrency(projectedValue)}</span> generates{" "}
                <span className="tnum">{fmtCurrency(annualInterest)}</span> per year in pure interest.
                Withdrawing at this rate is sustainable indefinitely — the years input barely matters because
                the principal isn't depleting. Try dropping the expected return to 5–7% to see realistic
                drawdown numbers.
              </p>
            </div>
          );
        })()}

        {/* Drawdown controls */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-arc-line">
          <label className="flex flex-col">
            <span className={`${LABEL} mb-2 text-center`}>Drawdown Period (years)</span>
            <input
              type="text"
              inputMode="numeric"
              value={drawdownYearsText}
              onChange={(e) => {
                const txt = e.target.value;
                setDrawdownYearsText(txt);
                if (txt === "") return;
                const n = parseInt(txt);
                if (!isNaN(n) && n > 0) setDrawdownYears(n);
              }}
              onBlur={() => {
                const n = parseInt(drawdownYearsText);
                if (isNaN(n) || n < 1) {
                  setDrawdownYearsText("30");
                  setDrawdownYears(30);
                } else {
                  setDrawdownYearsText(String(n));
                }
              }}
              className="px-3 py-2 rounded-arc-ctl text-sm tnum"
            />
          </label>
          <label className="flex flex-col">
            <span className={`${LABEL} mb-2 text-center`}>Expected Return During Drawdown (%)</span>
            <input
              type="text"
              inputMode="decimal"
              value={drawdownReturnText}
              onChange={(e) => {
                const txt = e.target.value;
                setDrawdownReturnText(txt);
                // Allow partial input like "" or "."
                if (txt === "" || txt === "." || txt === "-") return;
                const n = parseFloat(txt);
                if (!isNaN(n)) setDrawdownReturn(Math.max(0, n / 100));
              }}
              onBlur={() => {
                const n = parseFloat(drawdownReturnText);
                if (isNaN(n) || n < 0) {
                  setDrawdownReturnText("6.0");
                  setDrawdownReturn(0.06);
                } else {
                  setDrawdownReturnText(n.toFixed(1));
                }
              }}
              className="px-3 py-2 rounded-arc-ctl text-sm tnum"
            />
            {/* Reset-to-default helper, especially useful if user has set
                an unrealistic high return rate */}
            {drawdownReturn !== 0.06 && (
              <button
                type="button"
                onClick={() => {
                  setDrawdownReturn(0.06);
                  setDrawdownReturnText("6.0");
                }}
                className="self-start mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-semibold text-arc-ink-4 hover:text-arc-warn transition-colors"
                title="Reset to 6% — standard Fidelity / KeyBank planner default"
              >
                <RotateCcw size={11} /> Reset to 6%
              </button>
            )}
          </label>
        </div>

        <p className="mt-4 text-[10px] uppercase tracking-[0.14em] font-semibold text-arc-ink-4 leading-relaxed max-w-prose mx-auto text-center">
          Your withdrawal number above uses standard amortization math: PMT = P · r ÷ (1 − (1 + r)⁻ⁿ).
          The benchmark rules use Bengen's historical research and don't depend on your inputs — they're
          there as a reality check.
        </p>
      </Card>
    </div>
  );
};

export default RetirementTab;
